import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Amount, ConfirmModal, CustomSelect, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, Input, LoadingButton } from '@redpanda/ui';
import { formatTableDate, useNumberInput } from '@redpanda/utils';
import { setRecurringBilling, clearRecurringBilling } from '../services/api';
import { notifyDataChanged } from '../utils/dataEvents';
import { PMProject, RecurringInterval } from '../types/pm';
import { toDateInput, toISOString, getTodayDateInput } from '../utils/dateUtils';

interface Props {
  project: PMProject;
  /** 管理員才能設定（會自動產生應收）；一般成員只看得到說明 */
  canEdit: boolean;
  onChange: (project: PMProject) => void;
}

interface FormData {
  amount: number | '';
  interval: RecurringInterval;
  name: string;
  startDate: string;
  endDate: string;
}

const INTERVALS: RecurringInterval[] = ['MONTHLY', 'QUARTERLY', 'YEARLY'];

/**
 * 定期收費（維護、代管等月費合約）：每期開始日自動建立一個分包（例：「維護費 2026-11」），出現在應收帳款「可請款」
 * 起始日在過去時會補建到今天為止的各期
 */
const RecurringBillingControl: React.FC<Props> = ({ project, canEdit, onChange }) => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const [dialog, setDialog] = useState<'edit' | 'clear' | null>(null);
  const [saving, setSaving] = useState(false);
  const amountInput = useNumberInput('integer');
  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<FormData>();
  const tp = (k: string, options?: Record<string, unknown>) => t(`pmRecurring.${k}`, { ns: 'pages', ...options });
  const date = (value?: string | null) => (value ? formatTableDate(value, i18n.language) : '');

  const openEdit = () => {
    reset({
      amount: project.recurringAmount ?? '',
      interval: project.recurringInterval ?? 'MONTHLY',
      name: project.recurringName ?? tp('defaultName'),
      startDate: project.recurringStartDate ? toDateInput(project.recurringStartDate) : getTodayDateInput(),
      endDate: project.recurringEndDate ? toDateInput(project.recurringEndDate) : '',
    });
    setDialog('edit');
  };

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      const response = await setRecurringBilling(project.id, {
        amount: Number(data.amount),
        interval: data.interval,
        name: data.name.trim(),
        startDate: toISOString(data.startDate)!,
        endDate: data.endDate ? toISOString(data.endDate) : null,
      });
      onChange({ ...project, ...response.data });
      toast.success(t('common.messages.updateSuccess'));
      notifyDataChanged('packages'); // 新建立的各期分包：分包、應收帳款、合約總額跟著更新
      setDialog(null);
    } catch (error: any) {
      const code = error.response?.data?.code;
      toast.error(code === 'RECURRING_HAS_BUDGET' || code === 'RECURRING_HAS_MILESTONE_TERMS' ? tp(`errors.${code}`) : t('common.messages.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await clearRecurringBilling(project.id);
      onChange({ ...project, recurringAmount: null, recurringInterval: null, recurringName: null, recurringStartDate: null, recurringNextDate: null, recurringEndDate: null });
      toast.success(tp('stopped'));
      setDialog(null);
    } catch {
      toast.error(t('common.messages.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const active = !!project.recurringAmount;
  if (!active && !canEdit) return null;

  return (
    <>
      {active ? (
        <div className="mt-4 pt-4 border-t border-line flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm">
          <div className="min-w-0">
            <span className="font-medium text-fg">
              {tp('summary', { interval: tp(`interval.${project.recurringInterval}`), name: project.recurringName })} <Amount value={project.recurringAmount!} />
            </span>
            <span className="ml-2 text-fg-secondary">
              {project.recurringNextDate && tp('next', { date: date(project.recurringNextDate) })}
              {project.recurringEndDate && <>・{tp('until', { date: date(project.recurringEndDate) })}</>}
            </span>
          </div>
          {canEdit && (
            <div className="flex gap-3">
              <button type="button" onClick={openEdit} className="text-sm text-accent hover:underline">{t('common.actions.edit')}</button>
              <button type="button" onClick={() => setDialog('clear')} className="text-sm text-fg-secondary hover:text-red-600 dark:hover:text-red-400 hover:underline">{tp('stop')}</button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 text-right">
          <button type="button" onClick={openEdit} className="text-sm text-fg-secondary hover:text-fg hover:underline">{tp('set')}</button>
        </div>
      )}

      {dialog === 'edit' && (
        <DialogShell onClose={() => setDialog(null)} closeDisabled={saving} className="max-w-xl">
          <DialogHeader title={tp('title')} onClose={() => setDialog(null)} closeDisabled={saving} closeLabel={t('common.actions.close')} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-6 space-y-5">
              <p className="text-[13px] text-fg-secondary">{tp('hint')}</p>
              <div className="grid grid-cols-2 gap-4">
                <FormFieldWithError label={tp('fields.amount')} required error={errors.amount?.message} htmlFor="recurringAmount">
                  <Input
                    type="text"
                    id="recurringAmount"
                    {...amountInput.bind(register('amount', { required: t('common.forms.validation.required'), ...amountInput.validationRules }))}
                    variant={errors.amount ? 'error' : 'default'}
                    placeholder="0"
                  />
                </FormFieldWithError>
                <FormFieldWithError label={tp('fields.interval')} required htmlFor="recurringInterval">
                  <CustomSelect
                    id="recurringInterval"
                    value={watch('interval')}
                    onChange={(value) => setValue('interval', value as RecurringInterval)}
                    options={INTERVALS.map(i => ({ value: i, label: tp(`interval.${i}`) }))}
                  />
                </FormFieldWithError>
              </div>
              <FormFieldWithError label={tp('fields.name')} required error={errors.name?.message} htmlFor="recurringName">
                <Input
                  type="text"
                  id="recurringName"
                  {...register('name', { required: t('common.forms.validation.required'), maxLength: { value: 100, message: t('common.forms.validation.maxLength', { length: 100 }) } })}
                  variant={errors.name ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <div className="grid grid-cols-2 gap-4">
                <FormFieldWithError label={tp('fields.startDate')} required error={errors.startDate?.message} htmlFor="recurringStart">
                  <Input type="date" id="recurringStart" {...register('startDate', { required: t('common.forms.validation.required') })} variant={errors.startDate ? 'error' : 'default'} />
                </FormFieldWithError>
                <FormFieldWithError label={tp('fields.endDate')} error={errors.endDate?.message} htmlFor="recurringEnd">
                  <Input
                    type="date"
                    id="recurringEnd"
                    {...register('endDate', { validate: value => !value || value >= watch('startDate') || tp('endBeforeStart') })}
                    variant={errors.endDate ? 'error' : 'default'}
                  />
                </FormFieldWithError>
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setDialog(null)} className={dialogButtonClass.cancel} disabled={saving}>{t('common.actions.cancel')}</button>
              <LoadingButton type="submit" loading={saving} isEditMode t={t} />
            </DialogFooter>
          </form>
        </DialogShell>
      )}

      <ConfirmModal
        isOpen={dialog === 'clear'}
        onClose={() => setDialog(null)}
        onConfirm={clear}
        title={tp('stop')}
        message={tp('stopConfirm')}
        isLoading={saving}
        t={t}
      />
    </>
  );
};

export default RecurringBillingControl;
